import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { formatNumber, formatPeriod } from '../../kit/helpers'
import ActionDialog, { useActionSubmit } from './ActionDialog'
import {
  METRIC_LABELS,
  OPTIONAL_TRANSITION_METRICS,
  QUOTE_METRICS,
  isDateInput,
  toNumber,
} from './constants'

const TRANSITION_METRICS = [...QUOTE_METRICS, ...OPTIONAL_TRANSITION_METRICS]

const Heading = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: 'text.secondary',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    }}
  >
    {children}
  </Typography>
)

// The quote most recently recorded on a month that has not been reset.
const latestQuote = (records = []) =>
  records.find((record) => record.action?.quoteReference && record.action?.status !== 'voided')?.action ?? null

// The contract's standing total for a metric (included + additional, without
// complimentary capacity), as a starting value for the form.
const standingTotal = (allowance) => {
  const total = (Number(allowance?.included) || 0) + (Number(allowance?.additional) || 0)
  return total > 0 ? total : ''
}

// While a review action is open the form starts from the latest quote on
// record (the package being agreed); otherwise from the contract as it stands.
const initialForm = (panel) => {
  const quote = (panel?.requiredActions ?? []).length > 0 ? latestQuote(panel?.records) : null
  return {
    packageName: quote?.quotePackage ?? '',
    limits: Object.fromEntries(
      TRANSITION_METRICS.map((metric) => [
        metric,
        QUOTE_METRICS.includes(metric)
          ? (quote?.quoteAllowance?.[metric] ?? standingTotal(panel?.allowances?.[metric]))
          : '',
      ]),
    ),
    monthlyFee: '',
    annualFee: '',
    billingCadence: '',
    stripePriceId: '',
    stripeLookupKey: '',
    effectiveFrom: '',
    quoteReference: quote?.quoteReference ?? '',
    quoteAcceptedBy: '',
    quoteAcceptedAt: '',
    notes: '',
    stripeChange: '',
  }
}

// "Complete Enterprise+ transition": records the agreed contract variation -
// package, new TOTAL allowances, fees, the Stripe price and who accepted it.
// It applies the new allowances at once, closes every open action and starts
// a fresh review sequence. It does NOT change Stripe: staff change the
// subscription by hand and confirm here that they have.
const TransitionDialog = ({ open, tenant, panel, hasStripe, onOpenStripe, onClose, onSubmit }) => {
  const [form, setForm] = useState(() => initialForm(panel))
  const [attempted, setAttempted] = useState(false)
  const { busy, error, run } = useActionSubmit({ open, onSubmit, onClose })

  useEffect(() => {
    if (open) {
      setForm(initialForm(panel))
      setAttempted(false)
    }
  }, [open])

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))
  const setLimit = (metric, value) =>
    setForm((prev) => ({ ...prev, limits: { ...prev.limits, [metric]: value } }))

  const includedFor = (metric) => Number(panel?.allowances?.[metric]?.included) || 0

  // The server's rules (completeEnterprisePlusTransition), checked before
  // anything is sent.
  const limitProblem = (metric) => {
    const raw = String(form.limits[metric] ?? '').trim()
    if (raw === '') {
      return QUOTE_METRICS.includes(metric) ? 'Enter the new total.' : ''
    }
    const total = toNumber(raw)
    if (total === null || total <= 0) return 'Must be a number above zero.'
    if (metric !== 'controlRoomHoursPerMonth' && !Number.isInteger(total)) return 'Must be a whole number.'
    if (total < includedFor(metric)) {
      return `Below what the plan already includes (${formatNumber(includedFor(metric))}).`
    }
    return ''
  }
  const feeProblem = (value) => {
    if (String(value ?? '').trim() === '') return ''
    const fee = toNumber(value)
    return fee === null || fee < 0 ? 'Fees must be numbers, zero or above.' : ''
  }
  const problems = {
    packageName: form.packageName.trim() ? '' : 'Enter the Enterprise+ package name.',
    ...Object.fromEntries(TRANSITION_METRICS.map((metric) => [`limits.${metric}`, limitProblem(metric)])),
    monthlyFee: feeProblem(form.monthlyFee),
    annualFee: feeProblem(form.annualFee),
    stripePriceId: form.stripePriceId.trim() ? '' : 'Enter the Stripe price the subscription has been moved to.',
    effectiveFrom: isDateInput(form.effectiveFrom) ? '' : 'Enter the date the new fee takes effect.',
    quoteAcceptedBy: form.quoteAcceptedBy.trim() ? '' : 'Enter who accepted the variation for the customer.',
    quoteAcceptedAt: isDateInput(form.quoteAcceptedAt) ? '' : 'Enter the date the customer accepted the variation.',
    stripeChange: ['completed', 'scheduled'].includes(form.stripeChange)
      ? ''
      : 'Confirm whether the subscription change has been completed or scheduled in Stripe.',
  }
  const invalid = Object.values(problems).some(Boolean)
  const shown = (field) => (attempted ? problems[field] : '')

  // "Plan includes 500 · 1,000 additional" - what the new total will be stored as.
  const limitHelper = (metric) => {
    if (shown(`limits.${metric}`)) return shown(`limits.${metric}`)
    const included = includedFor(metric)
    const includedText = included > 0 ? `Plan includes ${formatNumber(included)}` : 'Not included in the plan'
    const total = toNumber(form.limits[metric])
    if (total === null) {
      return QUOTE_METRICS.includes(metric) ? includedText : `${includedText} · blank = unchanged`
    }
    return `${includedText} · ${formatNumber(Math.max(0, total - included))} additional`
  }

  const priceOptions = panel?.stripePriceOptions ?? []
  const pickedOption = priceOptions.find((option) => option.lookupKey === form.stripeLookupKey)
  const pickedPts = pickedOption?.limits?.ptsJourneysPerMonth
  const ptsMismatch =
    typeof pickedPts === 'number' &&
    toNumber(form.limits.ptsJourneysPerMonth) !== null &&
    toNumber(form.limits.ptsJourneysPerMonth) !== pickedPts

  const openPeriods = (panel?.requiredActions ?? []).map((action) => action.period)

  const confirm = () => {
    setAttempted(true)
    if (invalid) return
    const limits = {}
    for (const metric of TRANSITION_METRICS) {
      const total = toNumber(form.limits[metric])
      if (total !== null) limits[metric] = total
    }
    run({
      packageName: form.packageName.trim(),
      limits,
      ...(toNumber(form.monthlyFee) !== null ? { monthlyFee: toNumber(form.monthlyFee) } : {}),
      ...(toNumber(form.annualFee) !== null ? { annualFee: toNumber(form.annualFee) } : {}),
      ...(form.billingCadence ? { billingCadence: form.billingCadence } : {}),
      stripePriceId: form.stripePriceId.trim(),
      ...(form.stripeLookupKey ? { stripeLookupKey: form.stripeLookupKey } : {}),
      effectiveFrom: form.effectiveFrom,
      ...(form.quoteReference.trim() ? { quoteReference: form.quoteReference.trim() } : {}),
      quoteAcceptedBy: form.quoteAcceptedBy.trim(),
      quoteAcceptedAt: form.quoteAcceptedAt,
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      stripeChange: form.stripeChange,
    })
  }

  return (
    <ActionDialog
      open={open}
      title="Complete Enterprise+ transition"
      confirmLabel="Complete Enterprise+ transition"
      maxWidth="md"
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    >
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            endIcon={<OpenInNewIcon />}
            onClick={onOpenStripe}
            disabled={!hasStripe}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Open in Stripe
          </Button>
        }
      >
        <AlertTitle>AmbuNet does not change Stripe automatically</AlertTitle>
        Change {tenant?.name ? `${tenant.name}'s` : 'the'} subscription to the new price by hand in
        Stripe first (or schedule it there for the effective date), then record it here. Nothing in
        this form charges the customer or alters their subscription.
        {!hasStripe && ' No Stripe subscription or customer is recorded for this tenant yet.'}
      </Alert>

      <Heading>Package and new total allowances</Heading>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <TextField
            label="Enterprise+ package name"
            size="small"
            fullWidth
            required
            placeholder="e.g. Enterprise+ PTS 1,500"
            value={form.packageName}
            onChange={(event) => set('packageName', event.target.value)}
            error={!!shown('packageName')}
            helperText={shown('packageName')}
          />
        </Grid>
        {TRANSITION_METRICS.map((metric) => (
          <Grid item xs={12} md={4} key={metric}>
            <TextField
              label={`${METRIC_LABELS[metric]} - new total`}
              size="small"
              fullWidth
              required={QUOTE_METRICS.includes(metric)}
              type="number"
              inputProps={{ min: 0, step: metric === 'controlRoomHoursPerMonth' ? 'any' : 1 }}
              InputLabelProps={{ shrink: true }}
              placeholder={QUOTE_METRICS.includes(metric) ? '' : 'Unchanged'}
              value={form.limits[metric]}
              onChange={(event) => setLimit(metric, event.target.value)}
              error={!!shown(`limits.${metric}`)}
              helperText={limitHelper(metric)}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" display="block">
            Enter TOTALS, not increases. Each total is stored as the plan&apos;s included allowance
            plus the additional amount shown under the field. Employee, vehicle and base-location
            totals are only needed when they change.
          </Typography>
        </Grid>
      </Grid>

      <Heading>Fees</Heading>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Monthly fee (optional)"
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 'any' }}
            value={form.monthlyFee}
            onChange={(event) => set('monthlyFee', event.target.value)}
            error={!!shown('monthlyFee')}
            helperText={shown('monthlyFee') || `In ${tenant?.contract?.currency || 'GBP'}. Blank = unchanged.`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Annual fee (optional)"
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 'any' }}
            value={form.annualFee}
            onChange={(event) => set('annualFee', event.target.value)}
            error={!!shown('annualFee')}
            helperText={shown('annualFee') || 'Blank = unchanged.'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl size="small" fullWidth>
            <InputLabel shrink>Billing cadence</InputLabel>
            <Select
              value={form.billingCadence}
              label="Billing cadence"
              displayEmpty
              notched
              onChange={(event) => set('billingCadence', event.target.value)}
            >
              <MenuItem value="">Unchanged</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="annually">Annually</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Heading>Stripe price</Heading>
      <Stack gap={1}>
        {priceOptions.length > 0 && (
          <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Reusable prices:
            </Typography>
            {priceOptions.map((option) => {
              const picked = option.lookupKey === form.stripeLookupKey
              return (
                <Chip
                  key={option.lookupKey}
                  size="small"
                  clickable
                  color={picked ? 'primary' : 'default'}
                  variant={picked ? 'filled' : 'outlined'}
                  label={`${formatNumber(option.limits?.ptsJourneysPerMonth)} PTS journeys${option.billingCadence ? ` · ${option.billingCadence}` : ''}`}
                  title={option.lookupKey}
                  onClick={() => set('stripeLookupKey', picked ? '' : option.lookupKey)}
                  sx={{ borderRadius: 1.5 }}
                />
              )
            })}
          </Stack>
        )}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Stripe price ID"
              size="small"
              fullWidth
              required
              placeholder="price_…"
              value={form.stripePriceId}
              onChange={(event) => set('stripePriceId', event.target.value)}
              error={!!shown('stripePriceId')}
              helperText={shown('stripePriceId') || 'Copy it from the subscription in Stripe.'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Price lookup key (optional)"
              size="small"
              fullWidth
              placeholder="Pick a reusable price above, or type one"
              value={form.stripeLookupKey}
              onChange={(event) => set('stripeLookupKey', event.target.value)}
              helperText={
                ptsMismatch
                  ? `This price is for ${formatNumber(pickedPts)} PTS journeys a month - the new total above says ${formatNumber(toNumber(form.limits.ptsJourneysPerMonth))}.`
                  : 'Recorded alongside the price ID.'
              }
              FormHelperTextProps={ptsMismatch ? { sx: { color: '#B54708' } } : undefined}
            />
          </Grid>
        </Grid>
      </Stack>

      <Heading>Agreement with the customer</Heading>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <TextField
            type="date"
            label="Effective from"
            size="small"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={form.effectiveFrom}
            onChange={(event) => set('effectiveFrom', event.target.value)}
            error={!!shown('effectiveFrom')}
            helperText={shown('effectiveFrom') || 'When the new fee starts (billing or renewal date).'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Quote reference (optional)"
            size="small"
            fullWidth
            value={form.quoteReference}
            onChange={(event) => set('quoteReference', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4} />
        <Grid item xs={12} md={8}>
          <TextField
            label="Quote accepted by"
            size="small"
            fullWidth
            required
            placeholder="Name and role of the person at the customer"
            value={form.quoteAcceptedBy}
            onChange={(event) => set('quoteAcceptedBy', event.target.value)}
            error={!!shown('quoteAcceptedBy')}
            helperText={shown('quoteAcceptedBy')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            type="date"
            label="Accepted on"
            size="small"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={form.quoteAcceptedAt}
            onChange={(event) => set('quoteAcceptedAt', event.target.value)}
            error={!!shown('quoteAcceptedAt')}
            helperText={shown('quoteAcceptedAt')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Grid>
      </Grid>

      <Heading>Stripe subscription change - required</Heading>
      <FormControl error={!!shown('stripeChange')}>
        <RadioGroup value={form.stripeChange} onChange={(event) => set('stripeChange', event.target.value)}>
          <FormControlLabel
            value="completed"
            control={<Radio size="small" />}
            componentsProps={{ typography: { variant: 'body2' } }}
            label="I have changed the subscription by hand in Stripe"
          />
          <FormControlLabel
            value="scheduled"
            control={<Radio size="small" />}
            componentsProps={{ typography: { variant: 'body2' } }}
            label="I have scheduled the change by hand in Stripe for the effective date"
          />
        </RadioGroup>
        {shown('stripeChange') && (
          <Typography variant="caption" color="error">
            {shown('stripeChange')}
          </Typography>
        )}
      </FormControl>

      <Alert severity="info">
        Recording this applies the new allowances straight away, so the customer is not flagged
        while waiting for the billing date.{' '}
        {openPeriods.length > 0
          ? `It closes the open action${openPeriods.length === 1 ? '' : 's'} for ${openPeriods.map((period) => formatPeriod(period)).join(', ')} and starts a fresh review sequence from this month.`
          : 'It starts a fresh review sequence from this month.'}
      </Alert>
    </ActionDialog>
  )
}

export default TransitionDialog
